import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter,
  Package,
  AlertTriangle,
  ChevronDown,
  Edit,
  Save,
  X,
  Copy,
  Tag,
  RefreshCw
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { LabelQuantityDialog } from '@components/LabelQuantityDialog';
import { productService } from '../../services/productService';
import { Product, ProductFilters } from '../../types/product';

/**
 * Página de listagem de produtos conforme especificado no README
 * Inclui filtros, busca, paginação e ações baseadas em permissões
 */
export const ProductListView: React.FC = () => {
  const navigate = useNavigate();
  
  const [products, setProducts] = useState<(Product & Record<string, any>)[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  
  // Estados dos filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    is_active: true, // Ativo por padrão
    low_stock: false,
    out_of_stock: false,
    in_stock: false
  });
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;
  
  // Estatísticas rápidas
  const [totalProducts, setTotalProducts] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [outOfStockCount, setOutOfStockCount] = useState(0);

  // Label generation
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelProductId, setLabelProductId] = useState<number | null>(null);
  const [labelProductName, setLabelProductName] = useState<string>('');

  // Recalculate stock all
  const [recalculating, setRecalculating] = useState(false);

  // Carrega os produtos
  const loadProducts = async () => {
    console.log('📦 loadProducts starting, search:', search, 'currentPage:', currentPage);
    try {
      setLoading(true);
      setError(null);
      
      let productsArray: Product[] = [];
      let paginationData = null;
      
      const logNcmPresence = (arr: Product[]) => {
        if (!Array.isArray(arr)) return;
        const total = arr.length;
        const withNcm = arr.filter(p => p.ncm !== undefined && p.ncm !== null && p.ncm !== '').length;
        console.log(`🔎 NCM presence: ${withNcm}/${total} products have NCM`);
        if (withNcm < total) {
          console.log('🔎 Sample products without NCM:', arr.filter(p => !p.ncm).slice(0, 5));
        }
      };

      // Se há termo de busca, usa o endpoint de busca
      if (search.trim()) {
        try {
          const searchResult = await productService.searchProducts(search.trim(), currentPage, pageSize, filters);
          
          console.log('🔍 DEBUG - Search API Result:', searchResult);
          console.log('🔍 DEBUG - Search term:', search.trim());
          console.log('🔍 DEBUG - Search page:', currentPage);
          
          // O searchProducts agora retorna { products, pagination }
          if (searchResult && searchResult.products && Array.isArray(searchResult.products)) {
            productsArray = searchResult.products;
            paginationData = searchResult.pagination;
            
            console.log('🔍 DEBUG - Search products found:', productsArray.length);
            console.log('🔍 DEBUG - Search pagination:', paginationData);
          } else {
            productsArray = [];
            console.error('❌ Search result structure invalid:', searchResult);
          }
          
            // Para busca, usa paginação da API (não local)
          logNcmPresence(productsArray);
          setProducts((productsArray as Product[]).map(p => ({
            ...p,
            isEditingDescription: false,
            isEditingNcm: false,
            isEditingPrice: false,
            isEditingStatus: false,
            tempDescription: p.description,
            tempNcm: formatNCM(p.ncm || ''),
            tempPrice: p.price !== undefined && p.price !== null ? String(p.price) : '',
            tempActive: p.is_active
          })));
          setTotalProducts(paginationData?.total || paginationData?.total_items || productsArray.length);
          setTotalPages(paginationData?.total_pages || paginationData?.totalPages || 1);
          
          console.log('🔍 DEBUG - Search pagination processed:', {
            totalProducts: paginationData?.total || paginationData?.total_items || productsArray.length,
            totalPages: paginationData?.total_pages || paginationData?.totalPages || 1,
            currentPage: currentPage
          });
          
          // Se não encontrou resultados, usa fallback local
          if (productsArray.length === 0 && (paginationData?.total || 0) === 0) {
            console.error('❌ Search response is empty, falling back to local search');
            console.log('🔄 Attempting local search fallback...');
            
            // Fallback: buscar localmente se a API não retornar resultados
            const allProductsResponse = await productService.getProducts({ page: 1, pageSize: 1000 });
            let allProducts: Product[] = [];
            
            console.log('📄 Local search - API response:', allProductsResponse);
            
            if (Array.isArray(allProductsResponse)) {
              allProducts = allProductsResponse;
            } else if (allProductsResponse && typeof allProductsResponse === 'object' && 'data' in allProductsResponse) {
              const responseWithData = allProductsResponse as any;
              if (Array.isArray(responseWithData.data)) {
                allProducts = responseWithData.data;
              } else if (responseWithData.data && typeof responseWithData.data === 'object' && 'data' in responseWithData.data && Array.isArray(responseWithData.data.data)) {
                allProducts = responseWithData.data.data;
              }
            }
            
            console.log('📄 Local search - Found products:', allProducts.length);
            
            const searchTerm = search.trim().toLowerCase();
            const filteredProducts = allProducts.filter(product => 
              product.description?.toLowerCase().includes(searchTerm) ||
              product.sku?.toLowerCase().includes(searchTerm) ||
              product.ncm?.toLowerCase().includes(searchTerm)
            );
            
            console.log(`🔍 Local search - Filtered products for "${searchTerm}":`, filteredProducts.length);
            console.log('🔍 Local search - Sample filtered products:', filteredProducts.slice(0, 3));
            
            // Para fallback local, usa paginação local
            const startIndex = (currentPage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedResults = filteredProducts.slice(startIndex, endIndex);
            logNcmPresence(paginatedResults);
            setProducts((paginatedResults as Product[]).map(p => ({
              ...p,
              isEditingDescription: false,
              isEditingNcm: false,
              isEditingPrice: false,
              isEditingStatus: false,
              tempDescription: p.description,
              tempNcm: formatNCM(p.ncm || ''),
              tempPrice: p.price !== undefined && p.price !== null ? String(p.price) : '',
              tempActive: p.is_active
            })));
            setTotalProducts(filteredProducts.length);
            setTotalPages(Math.ceil(filteredProducts.length / pageSize));
          }
          
        } catch (searchError) {
          console.error('❌ Search API failed, falling back to local search:', searchError);
          
          // Fallback: buscar localmente se a API falhar  
          const allProductsResponse = await productService.getProducts({ page: 1, pageSize: 1000 });
          let allProducts: Product[] = [];
          
          if (Array.isArray(allProductsResponse)) {
            allProducts = allProductsResponse;
          } else if (allProductsResponse && typeof allProductsResponse === 'object' && 'data' in allProductsResponse) {
            const responseWithData = allProductsResponse as any;
            if (Array.isArray(responseWithData.data)) {
              allProducts = responseWithData.data;
            } else if (responseWithData.data && typeof responseWithData.data === 'object' && 'data' in responseWithData.data && Array.isArray(responseWithData.data.data)) {
              allProducts = responseWithData.data.data;
            }
          }
          
          const searchTerm = search.trim().toLowerCase();
          const filteredProducts = allProducts.filter(product => 
            product.description?.toLowerCase().includes(searchTerm) ||
            product.sku?.toLowerCase().includes(searchTerm) ||
            product.ncm?.toLowerCase().includes(searchTerm)
          );
          
          // Para fallback, usa paginação local
          const startIndex = (currentPage - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedResults = filteredProducts.slice(startIndex, endIndex);
          
          logNcmPresence(paginatedResults);
          setProducts((paginatedResults as Product[]).map(p => ({
            ...p,
            isEditingDescription: false,
            isEditingNcm: false,
            isEditingPrice: false,
            isEditingStatus: false,
            tempDescription: p.description,
            tempNcm: formatNCM(p.ncm || ''),
            tempPrice: p.price !== undefined && p.price !== null ? String(p.price) : '',
            tempActive: p.is_active
          })));
          setTotalProducts(filteredProducts.length);
          setTotalPages(Math.ceil(filteredProducts.length / pageSize));
        }
        
        console.log('🔍 DEBUG - Final search results:', {
          searchTerm: search.trim(),
          productsArray: productsArray,
          isArray: Array.isArray(productsArray),
          length: productsArray.length,
          currentPage: currentPage,
          totalProducts: paginationData?.total || paginationData?.total_items || productsArray.length,
          totalPages: paginationData?.total_pages || paginationData?.totalPages || 1
        });
      } else {
        // Sem busca, usa listagem normal com paginação da API
        const searchFilters: ProductFilters = {
          page: currentPage,
          pageSize: pageSize,
          ...filters // Aplicar filtros também na listagem normal
        };
        
        const response = await productService.getProducts(searchFilters);
        
        console.log('📄 DEBUG - Full API Response:', response);
        
        // Handle different possible response structures
        if (Array.isArray(response)) {
          // Direct array response
          productsArray = response;
        } else if (response && typeof response === 'object') {
          if (Array.isArray(response.data)) {
            // response.data is array
            productsArray = response.data;
            paginationData = response.pagination;
          } else if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray((response.data as any).data)) {
            // Nested structure: response.data.data is array
            productsArray = (response.data as any).data;
            paginationData = (response.data as any).pagination;
          } else if (response.data && typeof response.data === 'object' && 'items' in response.data && Array.isArray((response.data as any).items)) {
            // Check if response.data has pagination info and items
            productsArray = (response.data as any).items;
            paginationData = (response.data as any).pagination || response.data;
          }
        }
        
        logNcmPresence(productsArray);
        setProducts((productsArray as Product[]).map(p => ({
          ...p,
          isEditingDescription: false,
          isEditingNcm: false,
          isEditingPrice: false,
          isEditingStatus: false,
          tempDescription: p.description,
            tempNcm: formatNCM(p.ncm || ''),
          tempPrice: p.price !== undefined && p.price !== null ? String(p.price) : '',
          tempActive: p.is_active
        })));
        setTotalProducts(paginationData?.total || paginationData?.total_items || productsArray.length);
        setTotalPages(paginationData?.totalPages || paginationData?.total_pages || Math.ceil(productsArray.length / pageSize));
        
        console.log('📄 PAGINATION DEBUG - Processed Data:', {
          productsCount: productsArray.length,
          pagination: paginationData,
          totalProducts: paginationData?.total || paginationData?.total_items || productsArray.length,
          totalPages: paginationData?.totalPages || paginationData?.total_pages || Math.ceil(productsArray.length / pageSize)
        });
      }
      
      // Calculate statistics - para busca usa apenas produtos da página atual
      const statsProducts = productsArray;
      
      // Ensure statsProducts is an array before filtering
      if (Array.isArray(statsProducts)) {
        const lowStock = statsProducts.filter(p => 
          (p.stock_quantity || 0) > 0 && (p.stock_quantity || 0) <= (p.min_stock_quantity || 5)
        ).length;
        const outOfStock = statsProducts.filter(p => 
          (p.stock_quantity || 0) === 0
        ).length;
        
        setLowStockCount(lowStock);
        setOutOfStockCount(outOfStock);
      } else {
        setLowStockCount(0);
        setOutOfStockCount(0);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  // Carregamento inicial
  useEffect(() => {
    loadProducts();
  }, []);

  // Carrega produtos quando página ou filtros mudam
  useEffect(() => {
    loadProducts();
  }, [currentPage, filters]);

  // Busca com debounce quando o termo muda
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔄 Search term changed:', search);
      if (currentPage !== 1) {
        console.log('🔄 Resetting to page 1');
        setCurrentPage(1); // Vai triggerar o useEffect acima
      } else {
        console.log('🔄 Already on page 1, loading directly'); 
        loadProducts(); // Já está na página 1, executa diretamente
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [search]);

  // Fechar dropdown de filtros ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('[data-filter-trigger]')) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);



  // Renderiza badge de status do estoque disponível com cores conforme doc
  const getStockAvailableDisplay = (product: Product) => {
    const available = product.stock_quantity_available;
    const minStock = product.min_stock_quantity || 0;
    
    if (available === null || available === undefined) {
      return <span className="text-slate-400">—</span>;
    }
    
    if (available < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-red-600 font-medium" title="Overselling: mais produtos vendidos que o estoque disponível">
          <AlertTriangle size={14} />
          {available}
        </span>
      );
    }
    if (available === 0) {
      return <span className="text-amber-600 font-medium" title="Sem disponibilidade: todo o estoque está reservado">{available}</span>;
    }
    if (minStock > 0 && available < minStock) {
      return <span className="text-orange-600 font-medium" title="Estoque baixo: abaixo do mínimo configurado">{available}</span>;
    }
    return <span className="text-emerald-600 font-medium" title="Estoque adequado">{available}</span>;
  };

  // Recalcular estoque virtual de todos os produtos
  const handleRecalculateAll = async () => {
    if (!window.confirm('Deseja recalcular o estoque disponível de todos os produtos? Este processo será executado em segundo plano.')) return;
    try {
      setRecalculating(true);
      const message = await productService.recalculateStockAll();
      alert(message);
      // Recarregar a listagem após 5 segundos
      setTimeout(() => loadProducts(), 5000);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao iniciar recálculo');
    } finally {
      setRecalculating(false);
    }
  };

  // Formatação do NCM conforme padrão XXXX.XX.XX (mesma do ProductFormView)
  const formatNCM = (value: string): string => {
    const clean = (value || '').replace(/\D/g, '');
    if (clean.length <= 4) return clean;
    if (clean.length <= 6) return `${clean.slice(0,4)}.${clean.slice(4)}`;
    return `${clean.slice(0,4)}.${clean.slice(4,6)}.${clean.slice(6,8)}`;
  };

  // Helpers para edição inline
  const startEditDescription = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingDescription: true, tempDescription: p.description } : p));
  };

  const cancelEditDescription = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingDescription: false, tempDescription: p.description, hasError: false } : p));
  };

  const saveDescription = async (id: number) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    const newDesc = (product.tempDescription || '').trim();
    if (!newDesc) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, hasError: true, errorMessage: 'Descrição não pode ficar vazia' } : p));
      return;
    }

    try {
      await productService.updateProduct(id, { description: newDesc });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, description: newDesc, isEditingDescription: false, hasError: false } : p));
    } catch (err) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar descrição' } : p));
    }
  };

  const startEditNcm = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingNcm: true, tempNcm: formatNCM(p.ncm || '') } : p));
  };

  const cancelEditNcm = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingNcm: false, tempNcm: formatNCM(p.ncm || '') , hasError: false} : p));
  };

  const saveNcm = async (id: number) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    const newNcm = (product.tempNcm || '').trim();

    try {
      await productService.updateProduct(id, { ncm: newNcm || null });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, ncm: newNcm || undefined, isEditingNcm: false, hasError: false } : p));
    } catch (err) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar NCM' } : p));
    }
  };

  const startEditPrice = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingPrice: true, tempPrice: p.price !== undefined && p.price !== null ? String(p.price) : '' } : p));
  };

  const cancelEditPrice = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingPrice: false, tempPrice: p.price !== undefined && p.price !== null ? String(p.price) : '', hasError: false } : p));
  };

  const savePrice = async (id: number) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    const parsed = parseFloat((product.tempPrice || '').replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, hasError: true, errorMessage: 'Preço inválido' } : p));
      return;
    }

    try {
      await productService.updateProduct(id, { price: parsed });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, price: parsed, isEditingPrice: false, hasError: false } : p));
    } catch (err) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar preço' } : p));
    }
  };

  const startEditStatus = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingStatus: true, tempActive: !!p.is_active } : p));
  };

  const cancelEditStatus = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditingStatus: false, tempActive: p.is_active } : p));
  };

  const saveStatus = async (id: number) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    try {
      await productService.updateProduct(id, { is_active: !!product.tempActive });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, is_active: !!product.tempActive, isEditingStatus: false, hasError: false } : p));
    } catch (err) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar status' } : p));
    }
  };

  const toggleVisibleOnCRM = async (id: number) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    const newValue = !product.is_visible_on_crm;
    try {
      await productService.updateProduct(id, { is_visible_on_crm: newValue });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, is_visible_on_crm: newValue } : p));
    } catch (err) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar visibilidade CRM' } : p));
    }
  };

  const handleKeyPressField = (e: React.KeyboardEvent, id: number, saveFn: (id: number) => Promise<void> | void, cancelFn: (id: number) => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // @ts-ignore
      saveFn(id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelFn(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-h1 mb-2">Produtos</h1>
          <p className="text-slate-600">
            Gerenciar catálogo de produtos, estoque e preços
          </p>
        </div>
        <PermissionGuard permission="products.create">
          <Button
            icon={<Plus size={20} />}
            onClick={() => navigate('/products/new')}
          >
            Novo Produto
          </Button>
        </PermissionGuard>
      </div>

      {/* Estatísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {totalProducts.toLocaleString('pt-BR')}
                </p>
                <p className="text-small text-slate-500">Total de produtos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{lowStockCount}</p>
                <p className="text-small text-slate-500">Estoque baixo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{outOfStockCount}</p>
                <p className="text-small text-slate-500">Sem estoque</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {totalProducts - lowStockCount - outOfStockCount}
                </p>
                <p className="text-small text-slate-500">Disponível</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e busca */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Input
                  placeholder="Buscar por nome, SKU ou descrição..."
                  value={search}
                  onChange={(e) => {
                    console.log('⌨️ Input onChange:', e.target.value);
                    setSearch(e.target.value);
                  }}
                  startIcon={<Search size={20} />}
                />
                {search && loading && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
              {search && (
                <p className="text-xs text-slate-500 mt-1">
                  Buscando por: <span className="font-medium">"{search}"</span>
                  {totalProducts >= 0 && !loading && (
                    <span> - {totalProducts} resultado{totalProducts !== 1 ? 's' : ''}</span>
                  )}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Button 
                  variant={Object.entries(filters).filter(([key, value]) => key !== 'is_active' && value === true).length > 0 || !filters.is_active ? "primary" : "outline"} 
                  size="sm" 
                  icon={<Filter size={16} />}
                  onClick={() => setShowFilters(!showFilters)}
                  data-filter-trigger
                >
                  Filtros
                  {Object.entries(filters).filter(([key, value]) => key !== 'is_active' && value === true).length > 0 || !filters.is_active ? (
                    <span className="ml-1 bg-white text-primary rounded-full px-1.5 py-0.5 text-xs font-medium">
                      {Object.entries(filters).filter(([key, value]) => key !== 'is_active' && value === true).length + (!filters.is_active ? 1 : 0)}
                    </span>
                  ) : null}
                  <ChevronDown size={14} className={`ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>
                
                {showFilters && (
                  <div className="filter-dropdown absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="p-4">
                      <h3 className="font-medium text-gray-900 mb-3">Filtrar produtos</h3>
                      
                      <div className="space-y-3">
                        {/* Status */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.is_active}
                                onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Produtos ativos</span>
                            </label>
                          </div>
                        </div>
                        
                        {/* Estoque */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Estoque</h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.in_stock}
                                onChange={(e) => setFilters(prev => ({ ...prev, in_stock: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Em estoque</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.low_stock}
                                onChange={(e) => setFilters(prev => ({ ...prev, low_stock: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Estoque baixo</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.out_of_stock}
                                onChange={(e) => setFilters(prev => ({ ...prev, out_of_stock: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Sem estoque</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFilters({
                              is_active: true,
                              low_stock: false,
                              out_of_stock: false,
                              in_stock: false
                            });
                          }}
                          className="flex-1"
                        >
                          Limpar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setShowFilters(false)}
                          className="flex-1"
                        >
                          Aplicar
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de produtos */}
      <Card>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Lista de Produtos</h2>
          <PermissionGuard permission="products.recalculate_stock_all">
            <Button
              variant="outline"
              size="sm"
              icon={<RefreshCw size={16} className={recalculating ? 'animate-spin' : ''} />}
              onClick={handleRecalculateAll}
              disabled={recalculating}
            >
              Recalcular Estoque Virtual
            </Button>
          </PermissionGuard>
        </div>
        {search && (
          <div className="px-6 pb-2 text-sm text-slate-600">
            Resultados da busca para: "<span className="font-medium">{search}</span>"
            {totalProducts > 0 && (
              <span className="text-slate-500">
                {" - "}
                {totalProducts} produto{totalProducts !== 1 ? 's' : ''} encontrado{totalProducts !== 1 ? 's' : ''}
                {totalPages > 1 && (
                  <span> (página {currentPage} de {totalPages})</span>
                )}
              </span>
            )}
          </div>
        )}
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando produtos...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadProducts} variant="outline" size="sm">
                Tentar novamente
              </Button>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado'}
              </p>
              <PermissionGuard permission="products.create">
                <Button onClick={() => navigate('/products/new')}>
                  Cadastrar primeiro produto
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">SKU</th>
                  <th className="text-left">Produto</th>
                  <th className="text-left">NCM</th>
                  <th className="text-right">Preço</th>
                  <th className="text-right">Custo</th>
                  <th className="text-center">Est. Físico</th>
                  <th className="text-center" title="Estoque disponível para venda, descontando reservas de pedidos pendentes. Pode ser negativo se houver mais pedidos que estoque.">Est. Disponível</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">CRM</th>
                  <th className="text-center">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                        {product.sku}
                      </code>
                    </TableCell>
                    <TableCell>
                      {product.isEditingDescription ? (
                        <div className="flex items-center gap-2">
                          <input
                            className="border rounded px-2 py-1 w-full"
                            value={product.tempDescription}
                            onChange={(e) => setProducts(prev => prev.map(p => p.id === product.id ? { ...p, tempDescription: e.target.value, hasError: false } : p))}
                            onKeyDown={(e) => handleKeyPressField(e, product.id, saveDescription, cancelEditDescription)}
                          />
                          <button onClick={() => saveDescription(product.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditDescription(product.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{product.description}</p>
                            {product.is_manufactured === true && (
                              <span
                                className="inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                title="Produto de fabricação própria (atelier)"
                              >
                                Fabricação própria
                              </span>
                            )}
                          </div>
                          <button onClick={() => startEditDescription(product.id)} className="p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {product.isEditingNcm ? (
                        <div className="flex items-center gap-2">
                          <input
                            className="border rounded px-2 py-1 w-36"
                            value={product.tempNcm}
                            onChange={(e) => setProducts(prev => prev.map(p => p.id === product.id ? { ...p, tempNcm: formatNCM(e.target.value), hasError: false } : p))}
                            onKeyDown={(e) => handleKeyPressField(e, product.id, saveNcm, cancelEditNcm)}
                          />
                          <button onClick={() => saveNcm(product.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditNcm(product.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            {product.ncm ? (
                              <span className="text-sm text-slate-700">{product.ncm}</span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </div>
                          <button onClick={() => startEditNcm(product.id)} className="p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {product.isEditingPrice ? (
                        <div className="flex items-center justify-end gap-2">
                          <input
                            className="border rounded px-2 py-1 w-28 text-right"
                            value={product.tempPrice}
                            onChange={(e) => setProducts(prev => prev.map(p => p.id === product.id ? { ...p, tempPrice: e.target.value, hasError: false } : p))}
                            onKeyDown={(e) => handleKeyPressField(e, product.id, savePrice, cancelEditPrice)}
                          />
                          <button onClick={() => savePrice(product.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditPrice(product.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end">
                          <p className="font-medium">
                            {typeof product.price === 'number' ? product.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}
                          </p>
                          <button onClick={() => startEditPrice(product.id)} className="ml-2 p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        {typeof product.cost === 'number' ? (
                          <p className="font-medium">
                            {product.cost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-500">-</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div>
                        <p className="font-medium">{product.stock_quantity ?? '—'}</p>
                        {product.min_stock_quantity != null && (
                          <p className="text-xs text-slate-500">
                            Min: {product.min_stock_quantity}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {getStockAvailableDisplay(product)}
                    </TableCell>
                    <TableCell className="text-center">
                      {product.isEditingStatus ? (
                        <div className="flex items-center justify-center gap-2">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!product.tempActive} onChange={(e) => setProducts(prev => prev.map(p => p.id === product.id ? { ...p, tempActive: e.target.checked } : p))} />
                            <span className="text-sm">Ativo</span>
                          </label>
                          <button onClick={() => saveStatus(product.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditStatus(product.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {product.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                          <button onClick={() => startEditStatus(product.id)} className="p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => toggleVisibleOnCRM(product.id)}
                        className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          product.is_visible_on_crm !== false
                            ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                        title={product.is_visible_on_crm !== false ? 'Visível no CRM — clique para ocultar' : 'Oculto no CRM — clique para exibir'}
                      >
                        {product.is_visible_on_crm !== false ? 'Sim' : 'Não'}
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/products/${product.id}`)}
                          className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-slate-700 bg-transparent border border-slate-300 hover:bg-slate-50 focus:ring-emerald-500 px-3 py-1.5 text-sm"
                          aria-label={`Visualizar produto ${product.sku}`}
                        >
                          <span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye">
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </span>
                        </button>

                        <PermissionGuard permission="products.create">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const prod = await productService.getProductById(product.id);
                                navigate('/products/new', { state: { cloneData: prod } });
                              } catch (err) {
                                console.error('Erro ao buscar produto para clonar', err);
                                alert(err instanceof Error ? err.message : 'Erro ao clonar produto');
                              }
                            }}
                            className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 text-slate-700 bg-transparent border border-slate-300 hover:bg-slate-50 px-2 py-1 text-sm"
                            title={`Clonar produto ${product.sku}`}
                          >
                            <Copy size={14} />
                          </button>
                        </PermissionGuard>

                        <button
                          type="button"
                          onClick={() => {
                            setLabelProductId(product.id);
                            setLabelProductName(product.description || product.sku || '');
                            setLabelDialogOpen(true);
                          }}
                          className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 text-slate-700 bg-transparent border border-slate-300 hover:bg-slate-50 px-2 py-1 text-sm"
                          title={`Gerar etiquetas para ${product.sku}`}
                        >
                          <Tag size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
          
          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-gray-500">
                Mostrando {Math.min((currentPage - 1) * pageSize + 1, totalProducts)} a {Math.min(currentPage * pageSize, totalProducts)} de {totalProducts} produtos
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || loading}
                >
                  Anterior
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "primary" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        disabled={loading}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages || loading}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <LabelQuantityDialog
        isOpen={labelDialogOpen}
        onClose={() => {
          setLabelDialogOpen(false);
          setLabelProductId(null);
          setLabelProductName('');
        }}
        onConfirm={async (quantity) => {
          if (!labelProductId) return;
          try {
            setLabelLoading(true);
            await productService.downloadLabels(labelProductId, quantity);
            setLabelDialogOpen(false);
            setLabelProductId(null);
            setLabelProductName('');
          } catch (err) {
            alert(err instanceof Error ? err.message : 'Erro ao gerar etiquetas');
          } finally {
            setLabelLoading(false);
          }
        }}
        loading={labelLoading}
        productName={labelProductName}
      />
    </div>
  );
};